import { useQuery } from '@tanstack/react-query';
import { getRolloutToken } from '../../api/rollout';

interface UseRolloutTokenOptions {
  /** Organization ID to fetch token for */
  orgId: string | undefined;
  /** Current authorized account, session epoch, and role for this capability. */
  authContext: {
    uid: string | undefined;
    userId: string | undefined;
    sessionEpoch: number;
    role: string | null;
  };
  /** Whether the query should be enabled (e.g., when modal is open) */
  enabled?: boolean;
}

/**
 * React Query hook for fetching Rollout token
 * Eliminates the need for useEffect-based data fetching
 *
 * @example
 * const { data: token, isLoading, error } = useRolloutToken({
 *   orgId: selectedOrg?.id,
 *   authContext: { uid, userId, sessionEpoch, role },
 *   enabled: isOpen && isAdmin,
 * });
 */
export const useRolloutToken = ({ orgId, authContext, enabled = true }: UseRolloutTokenOptions) => {
  const authorized = authContext.role === 'super_admin' || authContext.role === 'org_admin';
  return useQuery({
    queryKey: ['rollout-token', orgId, authContext.uid, authContext.userId,
      authContext.sessionEpoch, authContext.role],
    queryFn: async () => {
      if (!orgId || !authContext.uid || !authContext.userId || !authorized) {
        throw new Error('Authorized organization administrator is required');
      }
      const response = await getRolloutToken(orgId);
      return response.token;
    },
    enabled: enabled && !!orgId && !!authContext.uid && !!authContext.userId && authorized,
    // Browser Rollout JWTs expire after 60 seconds; renew while the widget is open.
    staleTime: 25_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    gcTime: 60_000,
    retry: 1,
  });
};
